import * as i18next from 'i18next';
import * as Raven from 'raven-js';

import { Hooks } from './utils/Hooks';
import { Application, RELEASE_VERSION, SENTRY_URL, RELEASE_COMMIT } from './App';

// Submodules
import { enterFullscreen, setDarkStatusbar } from './submodules/app-api/AppAction';

import utils = egr.wcf.utils;
import { getEnvVar } from './submodules/utils/ReactScriptHelper';
import { AppDispatcher } from './dispatcher/AppDispatcher';
import { registerLogHandler, BaseMessage } from './submodules/app-api/Communication';
import { createConsoleOverlay } from './submodules/utils/Debug';
import { KompanInterface } from './Kompan';

declare global {
    interface Window {
        TestApp: Application | undefined;
        _log_args: () => void;
        WindowsAppMessageReceiver: (data: string) => void;
        WindowsAppMessageSender: {
            trigger: (message: string) => void;
            getVersion: () => string;
        };
    }

    interface JQuery {
        scrollbar: (parameter: any) => void;
        scrollTop: (parameter?: any) => any;
        scroll: () => void;
        find: (parameter?: any) => any;
        hasClass: (name: string) => boolean;
        fadeTo: (arg1: string | number, arg2: number, cb: any) => any;
        length: any;
    }

    interface JQueryAjaxSettings {}

    interface JQueryXHR {}

    interface JQueryEventObject {}
}

if (
    utils.string.isNotNullOrEmpty(RELEASE_VERSION) &&
    utils.string.isNotNullOrEmpty(SENTRY_URL)
) {
    // tslint:disable-next-line:no-any
    const r: any = Raven.config(SENTRY_URL, { release: RELEASE_VERSION + (RELEASE_COMMIT ? '-' + RELEASE_COMMIT : '') }).install();

    // Monkey patch raven in order to specify the exception context
    // as Error property.
    // Note: this can be used wherever `Raven.context` doens't work
    // e.g: Errors which are passed to the promise `reject` function
    const captureException: any = r.captureException.bind(r);

    r.captureException = (ex, options) => {
        if (ex != null && ex.exOptions != null) {
            options = jQuery.extend({}, options, ex.exOptions);
        }
        captureException(ex, options);
    };

    (window as any).onunhandledrejection = (error) => {
        Raven.captureException(error.reason);
    };
}

// @ts-ignore
utils.async.ajaxDefaultOptions.timeout = 180000;

window._log_args = function _log_args(): void {
    // tslint:disable-next-line:no-console
    console.log(Array.prototype.slice.call(arguments));
};

if (utils.string.isNotNullOrEmpty(getEnvVar('DEBUG_CONSOLE_OVERLAY'))) {
    createConsoleOverlay(2500);
}

if (utils.string.isNotNullOrEmpty(getEnvVar('DEBUG_DISPATCHER_ACTIONS'))) {
    AppDispatcher.register((payload: any): void => {
        // tslint:disable-next-line:no-console
        console.log({
            type: 'dispatcher',
            payload
        });
    });
}

if (utils.string.isNotNullOrEmpty(getEnvVar('DEBUG_APP_COMMUNICATION', ''))) {
    registerLogHandler((msg: BaseMessage): void => {
        // tslint:disable-next-line:no-console
        console.log(msg);
    });
}

const onloadPromise: Promise<void> = new Promise((resolve, reject) => { (window as any).onload = resolve; });

export let i18n: i18next.I18n;

(async (): Promise<void> => {
    setDarkStatusbar();

    let translationPromise: Promise<{lng: string, res?: any}>;

    const fallbackTranslation: {lng: string} = {lng: 'en'};
    const userLang: string | null = KompanInterface.initLanguage();

    if (userLang != null) {
        translationPromise = new Promise((resolve, reject) => {
            jQuery.ajax({
                url: 'i18n/' + userLang + '/translation.json',
                dataType: 'json'
            }).done((data) => { resolve({lng: userLang, res: data}); }
            ).fail(() => {
                resolve(fallbackTranslation);
            });
        });
    } else {
        translationPromise = Promise.resolve(fallbackTranslation);
    }

    // toLocaleString polyfill (for currency and date converstion)
    const supportsToLocaleStringOptions: boolean = !!(typeof Intl === 'object' && Intl && typeof Intl.NumberFormat === 'function');

    let toLocaleStringPolyfillPromise: Promise<void>;

    if (!supportsToLocaleStringOptions) {
        toLocaleStringPolyfillPromise = new Promise((resolve, reject) => {
            jQuery.ajax({
                url: 'scripts/intl/Intl.min.js',
                dataType: 'script'
            }).done(() => {
                jQuery.ajax({
                    url: `scripts/intl/locale-data/${userLang}.js'`,
                    dataType: 'script'
                }).done(() => { resolve(); }).fail(() => { resolve(); });
            }).fail(() => {
                jQuery.ajax({
                    url: 'scripts/intl/locale-data/en.js',
                    dataType: 'script'
                }).done(() => { resolve(); }).fail(() => { resolve(); });
            });
        });
    } else {
        toLocaleStringPolyfillPromise = Promise.resolve();
    }

    await Promise.all([
        enterFullscreen(),
        onloadPromise,
        toLocaleStringPolyfillPromise,
        translationPromise.then((settings) => {
            return new Promise((resolve, reject) => {
                const mapping: {} = {};
                if (settings.res != null) {
                    mapping[settings.lng] = {
                        translation: settings.res
                    };
                }

                i18n = i18next.init(
                    {
                        lng: settings.lng,
                        resources: mapping,
                        returnEmptyString: false,
                        nsSeparator: '',
                        keySeparator: ''
                    },
                    resolve as any
                );
            });
        }),
        Hooks.preAppInstantiationHook()
    ]);

    window.TestApp = new Application();
})();


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/index.tsx